// Server-side authorization probe. Signing in with Cognito only proves identity, not that
// this account is authorized for THIS portal — so before rendering the authenticated shell we hit
// /v1/command/me and gate on the result. A 200 means the API authorized us (command/analyst
// audience, MFA, staff domain); anything else means signed-in-but-not-authorized → the rejection
// wall. The dev path already did this; this makes the Cognito path do the same.
import { API_ORIGIN } from "../config";

export type Authz = "checking" | "authorized" | "denied";

export interface ProbeResult {
  authz: Authz;
  role: string | null;
}

const ME_URL = `${API_ORIGIN}/v1/command/me`;

/** Probe the portal's /me. `getToken` is supplied on the Cognito path (bearer); omitted on the dev
 *  path (the session cookie rides on credentials: "include"). Never throws — a network error or a
 *  non-2xx both resolve to "denied" so the gate fails closed (no shell on an unverified session). */
export async function probeCommandAuthz(
  getToken?: () => Promise<string | null>,
): Promise<ProbeResult> {
  try {
    const headers: Record<string, string> = {};
    if (getToken) {
      const token = await getToken();
      if (token) headers.Authorization = `Bearer ${token}`;
    }
    const res = await fetch(ME_URL, { credentials: "include", headers });
    if (!res.ok) return { authz: "denied", role: null };
    const body = (await res.json().catch(() => ({}))) as { data?: { role?: string } };
    return { authz: "authorized", role: body.data?.role ?? null };
  } catch {
    return { authz: "denied", role: null };
  }
}

// Server-confirmed role → human label for the shell. Command users are command (Operator)
// or analyst (Analyst) — the old hardcoded "Operator" couldn't tell them apart.
const ROLE_LABEL: Record<string, string> = {
  doctor: "Provider",
  command: "Operator",
  analyst: "Analyst",
};

export function roleLabel(role: string | null): string {
  return (role && ROLE_LABEL[role]) || "Operator";
}
